/* global Promise */

import React from 'react';
import PropTypes from 'prop-types';

import { showInteraction, unmountInteraction } from './interactions/InteractionDisplay';
import { showMedia, unmountMedia } from './media/MediaDisplay';
import interactionVisibilityService from '../../services/questionView/InteractionVisibilityService';
import showHiddenInteractionPlaceholder from './interactions/HiddenInteractionDisplay';
import { renderAudioElements } from './interactions/choiceInteraction/ChoiceInteraction';
import { reportError } from '../../services/ErrorReporting';
import QuestionInErrorState from './QuestionInErrorState';
import objectTypes from '../../constants/ObjectTypes';
import sessionReader from '../../store/readHelpers/SessionReader';
import customFieldsService from '../../services/CustomFieldsService';
import MathDisplay from './mathDisplay/MathDisplay';
import patchSuperscriptAccessibility from './mathDisplay/patchSuperscriptAccessibility';

import styles from './QTIAssessmentItem.scss';
import questionReader from '../../store/readHelpers/QuestionReader';
import uiService from '../../services/UIService';
import questionSelectionService from '../../services/QuestionSelectionService';
import feedbackService from '../../services/FeedbackService';
import constants from '../../qti/Constants';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import SpreadSheetInteraction from './interactions/spreadSheetInteraction/SpreadSheetInteraction';
import settingsReader from '../../store/readHelpers/SettingsReader';
import responseService from '../../services/ResponseService';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import domService from '../../services/DomService';

class QTIAssessmentItem extends React.Component {
    static propTypes = {
        /* eslint-disable react/no-unused-prop-types */
        // the callback function for writing back results callback(responseIdentifier, [response])
        callback: PropTypes.func.isRequired,

        /*
         OPTIONAL provided if the interaction supports the shuffling of choices and this is turned on.
         The choices should then be rendered in that order
         */
        choiceSequences: PropTypes.object,

        // The context in which the question is displayed. Eg: 'exam' or 'preview'.
        context: PropTypes.string.isRequired,

        // OPTIONAL the correct responses as an object with responseIdentifier as map key
        // correctResponses is only provided in read only mode when info about correct/wrong answers should be shown
        correctResponses: PropTypes.object,
        reportReady: PropTypes.func.isRequired,

        // The questions part of the interactionVisibilityState
        questionInteractionVisibility: PropTypes.object,

        isCurrent: PropTypes.bool,
        currentScorableItem: PropTypes.number,

        isPreRender: PropTypes.bool,
        isReadOnly: PropTypes.bool,
        lockedMedia: PropTypes.bool,

        // OPTIONAL object that contains the score and feedback for the question and each of the interactions
        outcomeVariables: PropTypes.object,

        // the object output from QTIParser with {getComponentBodyAsHTML, getInteractions}
        question: PropTypes.object.isRequired,
        services: PropTypes.object.isRequired,

        // the user responses object with responseIdentifier as map key
        userResponses: PropTypes.object.isRequired,
        viewMedia: PropTypes.string,
        direction: PropTypes.string,

        onMediaPlayedToCompletion: PropTypes.func,
        onMediaPlayedCounterReset: PropTypes.func,

        feedback: PropTypes.object,
        shouldShowInteractionFeedback: PropTypes.bool,
        zoomFactor: PropTypes.string,
        useMCDiscreteWithOneImageMaxWidth: PropTypes.bool,
    };

    static filterOutDuplicateMediaModels(mediaModels) {
        return mediaModels.filter((outerModel, outerIndex, arr) => {
            // Filter out duplicate media models. They will have identical targets.
            return !arr.find((innerModel, innerIndex) => {
                return innerModel.target === outerModel.target
                    && innerIndex < outerIndex;
            });
        });
    }

    static getExtraResponses(extraResponseDeclarations, userResponses) {
        if (Array.isArray(extraResponseDeclarations)) {
            const extraResponses = [];

            for (const declaration of extraResponseDeclarations) {
                const response = userResponses[declaration.identifier];
                if (response) {
                    extraResponses.push({
                        [declaration.identifier]: response,
                    });
                }
            }

            return extraResponses;
        }

        return undefined;
    }

    constructor(props) {
        super(props);

        const { question } = props;
        this.state = {
            inQuestionErrorState: (question && question.documentParseError),
        };

        this.shouldRenderAudioFromAlternatives = !interactionVisibilityService
            .shouldShowInteractions(
                props.questionInteractionVisibility,
                props.viewMedia
            );

        this.rootElement = React.createRef();
        this.prepareAudioFilesForAutoPlay = this.prepareAudioFilesForAutoPlay.bind(this);
        this.mediaMagicApplied = false;
    }

    componentDidMount() {
        // initial draw, so set up the interactions and media
        this.updateContent(this.props, true);
        if (this.shouldRenderAudioFromAlternatives) {
            document.body.addEventListener('touchstart', this.prepareAudioFilesForAutoPlay, false);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const inQuestionErrorState = (nextProps.question && nextProps.question.documentParseError);

        // Reset error state on new question or get the new props tested
        this.setState({
            inQuestionErrorState,
        });

        if (this.areNextPropsDifferent(this.props, nextProps)) {
            // Do not update content if new props are the same as old props.
            this.updateContent(nextProps, false);
        }
    }

    shouldComponentUpdate() {
        // Do not re-render the component, since his will redraw the static html added in dangerouslySetInnerHTML
        // and then the interaction components will loose their parents
        return false;
    }

    componentWillUnmount() {
        this.unmountInteractionsAndMedia();
        if (this.shouldRenderAudioFromAlternatives) {
            document.body.removeEventListener('touchstart', this.prepareAudioFilesForAutoPlay, false);
            this.prepareAudioFilesForAutoPlay();
        }
    }

    /**
     * This is called in response to a 'touchstart' event and is needed to enable automatic playing of audio files in iOS.
     */
    prepareAudioFilesForAutoPlay() {
        if (this.mediaMagicApplied || typeof this.rootElement.current !== 'object') {
            return;
        }

        Array
            .from(
                this.rootElement.current.querySelectorAll('audio')
            )
            .forEach((audioElement) => {
                audioElement.play();
                audioElement.pause();
                audioElement.currentTime = 0;
            });

        this.mediaMagicApplied = true;
    }

    isInQuestionErrorState = () => {
        return this.state.inQuestionErrorState;
    };

    areNextPropsDifferent = (currentProps, nextProps) => {
        return JSON.stringify(currentProps) !== JSON.stringify(nextProps);
    };

    handleRenderError = (error) => {
        if (this.isInQuestionErrorState()) {
            return;
        }

        console.error(error);
        this.setState({ inQuestionErrorState: true });
        // have to forceUpdate since it can be called from componentDidMount
        this.forceUpdate();
    };

    unmountInteractionsAndMedia() {
        const { responseModels = [] } = this.props.question;
        responseModels.forEach(model => unmountInteraction(model.target));

        const { mediaModels = [] } = this.props.question;
        mediaModels.forEach(model => unmountMedia(model.target));
    }

    getOutcomeValueForInteraction = (prefix, identifier, props) => {
        const { outcomeVariables } = props;
        if (outcomeVariables) {
            const outcomeObject = outcomeVariables[`${prefix}_${identifier}`];
            return outcomeObject ? outcomeObject.value : undefined;
        }
        return undefined;
    };

    reportReady = (success) => {
        try {
            const { reportReady, question } = this.props;
            if (typeof reportReady === 'function') {
                reportReady(success, question.identifier);
            } else {
                reportError(`No reportReady on ${question.contentRevisionId}, ${question.objectType}, ${question.title}`);
            }
        } catch (error) {
            console.error(error);
        }
    };

    modifyResponseModelsForGapMatchQuestions() {
        const { responseModels } = this.props.question;
        let modifiedResponseModels = [...responseModels];

        if (responseModels.length > 1 && marketplacePropertiesService.isEnableTwoInlineGapMatchAreas()) {
            const areNotGapModels = responseModels.some(model => model.type !== constants.interactionType.GAP_MATCH_INTERACTION);
            if (!areNotGapModels) {
                modifiedResponseModels = responseModels.map((model, index) => {
                    if (index === 0) return { ...model, showKeyboardHelp: true, hideFooter: true };
                    if (index === responseModels.length - 1) return { ...model, showKeyboardHelp: false, hideFooter: false };
                    return { ...model, showKeyboardHelp: false, hideFooter: true };
                });
            }
        }

        return modifiedResponseModels;
    }

    async updateContent(props, isInitialMount) {
        const promises = [];

        if (interactionVisibilityService.shouldShowInteractions(props.questionInteractionVisibility, props.viewMedia)) {
            promises.push(
                this.updateInteractions(props)
                    .then(() => this.updateMathJax()) // MathJax must be updated after the interactions are ready.
            );
        } else {
            promises.push(
                this.showHiddenInteractionPlaceholders(props)
            );
        }

        if (isInitialMount && !sessionReader.isPrintTypeAnswersOnly()) {
            promises.push(
                this.updateMedia(props) // Media can be updated in parallel with the interactions. They handle their own media.
            );
        }

        return Promise
            .all(promises)
            .then(() => this.reportReady(true))
            .catch((error) => {
                this.reportReady(false);
                this.handleRenderError(error);
            });
    }

    async updateMathJax() {
        if (this.rootElement.current instanceof Element) {
            return MathDisplay.showMath(this.rootElement.current)
                .then(() => patchSuperscriptAccessibility(this.rootElement.current));
        }
        return Promise.resolve(false);
    }

    async updateInteractions(props) {
        const {
            choiceSequences,
            context,
            correctResponses,
            isPreRender,
            services,
            userResponses,
            viewMedia,
            direction,
            shouldShowInteractionFeedback,
        } = props;
        const { responseModels } = props.question;
        const skipUnansweredIndicator = context === 'preview' && viewMedia !== 'screen';
        const isSpreadsheetQuestion = questionReader.isSpreadSheetQuestion(this.props.question);

        if (!responseModels || this.isInQuestionErrorState() || isSpreadsheetQuestion) {
            return false;
        }

        const modifiedResponseModels = this.modifyResponseModelsForGapMatchQuestions();

        const allPromises = modifiedResponseModels.map((model) => {
            const { identifier } = model;
            const extraResponses = QTIAssessmentItem.getExtraResponses(model.extraResponseDeclarations, userResponses);
            const outcomeIsCorrect = this.getOutcomeValueForInteraction('isCorrect', identifier, props);
            const outcomeIsPartiallyCorrect = this.getOutcomeValueForInteraction('isPartiallyCorrect', identifier, props);
            const responseDeclaration = props.question.responseDeclarations.filter(declaration => declaration.identifier === identifier);
            const defaultValue = responseDeclaration.length ? responseDeclaration[0].defaultValue : [];
            const hasValidResponseFeedback = feedbackService.hasValidResponseFeedback(
                identifier, props.question, props.feedback, shouldShowInteractionFeedback
            );
            return showInteraction(
                model,
                props.question,
                userResponses[identifier] || [],
                this.interactionCallback,
                correctResponses ? (correctResponses[identifier] || correctResponses.evaluationTestCases) : undefined,
                choiceSequences ? choiceSequences[identifier] : undefined,
                extraResponses,
                outcomeIsCorrect,
                outcomeIsPartiallyCorrect,
                services,
                props.context,
                props.isReadOnly,
                props.isCurrent,
                props.flaggedItems,
                props.currentScorableItem,
                props.settingValues,
                props.viewMedia,
                skipUnansweredIndicator,
                isPreRender,
                defaultValue,
                direction,
                hasValidResponseFeedback,
                props.zoomFactor,
            );
        });

        return Promise.all(allPromises);
    }

    async showHiddenInteractionPlaceholders(props) {
        const { responseModels } = props.question;
        if (!responseModels || this.isInQuestionErrorState()) {
            return false;
        }

        const allPromises = responseModels.map((model) => {
            return showHiddenInteractionPlaceholder(model);
        });

        return Promise.all(allPromises);
    }

    async updateMedia(props) {
        const {
            isPreRender,
            lockedMedia = false,
            onMediaPlayedCounterReset,
            onMediaPlayedToCompletion,
            services,
            question: { displayContext, mediaModels, objectType },
            useMCDiscreteWithOneImageMaxWidth = false,
        } = props;

        if (!mediaModels || mediaModels.length === 0 || this.isInQuestionErrorState()) {
            return true;
        }

        const showMediaParams = {
            services,
            displayContext,
            mediaElement: undefined,
            isPreRender,
            lockedMedia,
            questionObjectType: objectType,
            onMediaPlayedToCompletion,
            onMediaPlayedCounterReset,
            useMCDiscreteWithOneImageMaxWidth,
        };

        const promises = QTIAssessmentItem
            .filterOutDuplicateMediaModels(mediaModels)
            .map(model => showMedia({ ...showMediaParams, model }));

        return Promise.all(promises);
    }

    interactionCallback = (identifier, response, extraResponses) => {
        const {
            callback,
            isPreRender,
            services,
            question: { contentRevisionId },
        } = this.props;

        const shouldAutoSelectQuestion = !isPreRender
            && !sessionReader.isPublicExam()
            && !sessionReader.isContextViewOwnDelivery()
            && questionSelectionService.isQuestionInSectionWithSelectionEnabled(contentRevisionId)
            && services.questionSelectionService.isUndecided(contentRevisionId);

        if (shouldAutoSelectQuestion) {
            services.questionSelectionService.autoSelectQuestion(contentRevisionId);
        }

        callback(contentRevisionId, identifier, response, extraResponses);
    };

    /**
     * This will render audio files that belong in choice question alternatives. The purpose is to have them available for
     * "activation" via a touchstart event before they are rendered inside the interaction.
     * See touchAudioFiles()
     * @param question
     * @param choiceSequences
     * @return {null|*}
     */
    renderAudioFromChoiceAlternatives(question, choiceSequences) {
        const { responseModels } = question;
        const { identifier } = responseModels[0];
        if ([objectTypes.MULTIPLE_CHOICE, objectTypes.MULTIPLE_RESPONSE].includes(question.objectType)) {
            return renderAudioElements(responseModels[0], choiceSequences[identifier]);
        }
        return null;
    }

    /**
     * Helper for renderInlineChoiceAlternatives: builds data for a single inline choice response model
     * for print-based CEQ rendering with horizontal layout.
     * @param {Object} responseModel - The response model containing alternatives and identifier.
     * @returns {Object} Object with identifier, interactionNumber, and alternatives.
     */
    buildInlineChoiceItemData(responseModel) {
        return {
            identifier: responseModel.identifier,
            interactionNumber: scorableItemNumberingService.getInteractionNumber(
                this.props.question.contentRevisionId,
                responseModel.identifier
            ),
            alternatives: responseService.getAlternatives(responseModel),
        };
    }

    getAlternativeClassName(alternative, correctResponse) {
        return alternative.identifier === correctResponse ? 'inline-correct-text' : 'inline-text';
    }

    /**
     * Renders the inline choice alternatives for paper-based printing with CEQ render style.
     * This is specifically for horizontal inline choice interactions, where alternatives are
     * displayed below the question body instead of as dropdown/select options.
     * @param {Object} question - The question object containing responseModels.
     * @param {Object} [correctResponses={}] - Map of response identifiers to correct answer identifiers.
     * @returns {JSX.Element|null} The rendered inline choice alternatives, or null if none.
    */
    renderInlineChoiceAlternatives(question, correctResponses = {}) {
        if (!question.responseModels || question.responseModels.length === 0) {
            return null;
        }

        const itemsData = question.responseModels.map((responseModel) => {
            const itemData = this.buildInlineChoiceItemData(responseModel);
            const correctResponse = correctResponses[responseModel.identifier];
            return {
                ...itemData,
                correctResponse: Array.isArray(correctResponse) ? correctResponse[0] : correctResponse,
            };
        });

        return (
            <div className={styles.InlineChoiceAlternativesWrapper}>
                {itemsData.map(itemData => (
                    <div
                        key={`inlineChoiceAlternatives_${itemData.identifier}`}
                        className={styles.inlineChoiceAlternatives}
                    >
                        <b>{itemData.interactionNumber}</b>
                        {itemData.alternatives.map(alternative => (
                            <span
                                key={`${itemData.identifier}_${alternative.identifier}`}
                                className={`${this.getAlternativeClassName(alternative, itemData.correctResponse)} ${styles.inlineChoiceAlternative}`}
                            >
                                <b className={styles.inlineChoiceAlternativeIdentifier}>
                                    {alternative.identifier}
                                </b>
                                {alternative.content}
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { choiceSequences, question, services, correctResponses } = this.props;
        const isSpreadsheetQuestion = questionReader.isSpreadSheetQuestion(this.props.question);
        const lang = services.i18nService.getIetfLanguageCode(question.language);
        const classNames = [styles.QTIAssessmentItem, 'preRender'];
        let trimmedQuestionComponentBody;
        if (settingsReader.shouldUsePaperBasedForMCQOrientation(question)) {
            trimmedQuestionComponentBody = domService.cleanEmptyspaces(question.componentBody);
        }
        const showInlineChoiceAlternatives = settingsReader.isPBRenderStyleEnabled()
            && sessionReader.isPrintStylePaperBased()
            && question.objectType === objectTypes.INLINE_CHOICE
            && question.responseModels?.[0]?.hasHorizontalRender;

        if (questionReader.isWordFormationQuestion(question)) {
            classNames.push(styles.ExtendedRenderingWordFormation);
        }

        if (uiService.shouldAddExtraLineSpacing(question)) {
            classNames.push('extraLineSpacing');
        }

        if (sessionReader.isPrintStylePaperBased()) {
            classNames.push(styles.pbComponentBody);
            if (customFieldsService.isMatchingFeature(question) ||
                customFieldsService.isMatchingPlanMapDiagramLabelling(question)
            ) {
                classNames.push(styles.pbMatchingComponentBody);
            }
            if (customFieldsService.isReading(question)) {
                classNames.push(styles.pbReadingComponentBody);
            }
        }

        if (this.isInQuestionErrorState()) {
            return <QuestionInErrorState question={question} services={services} />;
        }
        return (
            <div
                className={styles.QTIAssessmentItemWrapper}
                ref={this.rootElement}
            >
                {
                    this.shouldRenderAudioFromAlternatives
                        ? this.renderAudioFromChoiceAlternatives(question, choiceSequences)
                        : null
                }
                {
                    isSpreadsheetQuestion ? (
                        <SpreadSheetInteraction
                            model={{ itemBody: this.props.question.componentBody }}
                            contentRevisionId={this.props.question.contentRevisionId}
                        />
                    ) : (
                        <div
                            id={`QTI_renderer_${question.identifier}`}
                            className={classNames.join(' ')}
                            lang={lang}
                            dangerouslySetInnerHTML={{ __html: trimmedQuestionComponentBody || question.componentBody }}
                        />
                    )
                }
                {showInlineChoiceAlternatives && this.renderInlineChoiceAlternatives(question, correctResponses)}
            </div>
        );
    }
}

export default QTIAssessmentItem;
